import PlanetCanvas from './PlanetCanvas';
import DevelopmentPanel from '../development/DevelopmentPanel';
import TechTreePanel from '../techtree/TechTreePanel';
import CivilisationPanel from '../civilisation/CivilisationPanel';
import StatsPanel from '../stats/StatsPanel';
import { useUIStore } from '../../stores/uiStore';
import { useSoundStore } from '../../stores/soundStore';
import { useHintStore } from '../../stores/hintStore';
import { usePlanet } from '../../hooks/usePlanet';
import { useSpaceConquestMap } from '../../hooks/useSpaceConquest';
import { useInbox } from '../../hooks/useMessages';
import { Coins, Wrench, LogOut, Trophy, History, Volume2, VolumeX, Plus, Bug, Camera, Calendar, ChevronDown, Telescope, HeartPulse, ScrollText, Landmark, Home, X, Rocket, Network } from 'lucide-react';
import DevTimeControls from '../dev/DevTimeControls';
import { devApi } from '../../api/dev';
import { clearGameSession } from '../../api/client';
import { useQueryClient } from '@tanstack/react-query';
import LeaderboardPanel from '../leaderboard/LeaderboardPanel';
import ActivityPanel from '../activity/ActivityPanel';
import EventLogPanel from '../events/EventLogPanel';
import CreaturePanel from '../creatures/CreaturePanel';
import ToastContainer from '../toast/ToastContainer';
import StarMapModal from '../stars/StarMapModal';
import SpaceGameView from '../space/SpaceGameView';
import HintPanel from '../hints/HintPanel';
import EvolutionModal from '../hints/EvolutionModal';
import EmergencyModal from '../hints/EmergencyModal';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useCivilisation } from '../../hooks/useCivilisation';
import { formatEventEffects } from '../events/eventEffects';

const stageNames = [
  'Barren Planet',
  'Terraforming',
  'Spark of Life',
  'Plant Life',
  'Complex Life',
  'Civilisation',
  'Technology',
  'Space Technology',
  'Space Exploration',
];

export default function PlanetView() {
  const { data, isPending, isLoading, isError, error } = usePlanet();
  const {
    developmentOpen,
    openDevelopment,
    closeDevelopment,
    leaderboardOpen,
    openLeaderboard,
    closeLeaderboard,
    activityOpen,
    openActivity,
    closeActivity,
    creaturePanelOpen,
    openCreaturePanel,
    closeCreaturePanel,
    civilisationOpen,
    openCivilisation,
    closeCivilisation,
    starMapOpen,
    openStarMap,
    closeStarMap,
    eventLogOpen,
    openEventLog,
    closeEventLog,
    techTreeOpen,
    openTechTree,
    closeTechTree,
    closePanels,
  } = useUIStore();
  const { muted, toggleMute } = useSoundStore();
  const queryClient = useQueryClient();
  const isDev = import.meta.env.DEV;

  // All hooks must be called before any conditional returns
  const [evolutionOpen, setEvolutionOpen] = useState(false);
  const [emergencyOpen, setEmergencyOpen] = useState(false);
  const [dismissedEventId, setDismissedEventId] = useState<string | null>(null);
  const [showPlanetOverride, setShowPlanetOverride] = useState(false);
  const { showHint } = useHintStore();
  const prevStageRef = useRef<number | null>(null);

  const planet = data?.data?.planet;
  const inboxQuery = useInbox(!!planet);
  const civilisationQuery = useCivilisation(!!planet);
  const balance = data?.data?.balance;
  const stage = planet?.stage || 0;
  const spaceMapQuery = useSpaceConquestMap(!!planet && stage >= 8);
  const credits = balance?.current_balance || 0;
  const surfaceItems = useMemo(() => planet?.surface_items || [], [planet?.surface_items]);
  const visibleSurfaceItems = useMemo(() => surfaceItems.filter((item) => item.visible !== false), [surfaceItems]);
  const stats = planet?.state?.stats || {};
  const eventHistory = useMemo(() => (planet?.state as any)?.eventHistory || [], [planet?.state]);
  const currentDay = planet?.current_day || 1;
  const geography = planet?.state?.geography;
  const lifeChance = planet?.state?.life?.lifeChance;
  const statTrends = data?.data?.statTrends || {};
  const usableLand = geography?.usableLand ?? 0;
  const creatureDominance = ((planet?.state as any)?.creatureDominance as Record<string, number>) || {};
  const dominantCreature = Object.entries(creatureDominance).sort((a, b) => b[1] - a[1])[0]?.[0] || null;
  const visualState = planet?.visual_state || {};
  const planetConfig = (visualState as any)?.planetConfig || null;
  const evolutionKey = planet?.planet_id ? `evolution_shown_${planet.planet_id}` : null;

  const latestEvent = eventHistory.length ? eventHistory[eventHistory.length - 1] : null;
  const latestEventId = (latestEvent as any)?.eventId || (latestEvent as any)?.day || null;
  const isEventDismissed = latestEventId !== null && dismissedEventId === latestEventId;
  const latestEventText = latestEvent && !isEventDismissed ? String((latestEvent as any).message || (latestEvent as any).name || (latestEvent as any).type || 'Planet event detected') : null;
  const latestEventEffects = latestEvent && !isEventDismissed ? formatEventEffects(latestEvent as any) : '';
  const civilisationUnlocked = civilisationQuery.data?.data?.unlocked ?? false;
  const intelligentSpecies = civilisationQuery.data?.data?.civilisation?.ambassador?.baseSpecies || null;


  useEffect(() => {
    if (!planet) return;

    // Stage transition hints
    if (prevStageRef.current !== null && stage > prevStageRef.current) {
      if (stage === 1) {
        showHint({ id: 'stage-1', text: 'Great! Your planet is terraforming. Build water and atmosphere upgrades to prepare for life.', priority: 'medium' });
      } else if (stage === 2) {
        showHint({ id: 'stage-2', text: 'Life is possible! Buy Primordial Soup to start the spark of life.', priority: 'medium' });
      } else if (stage === 3) {
        showHint({ id: 'stage-3', text: 'Plants need land and oxygen. Build Coastal Land, then plant life upgrades.', priority: 'medium' });
      } else if (stage === 4) {
        showHint({ id: 'stage-4', text: 'Complex life emerges! Keep building animal life and habitat diversity.', priority: 'medium' });
      } else if (stage === 5) {
        showHint({ id: 'stage-5', text: 'Civilisation begins! Build settlements and farms to grow your population.', priority: 'high' });
      } else if (stage === 6) {
        showHint({ id: 'stage-6', text: 'Research unlocked! Open the Tech Tree (Tech button) and start researching towards the space age.', priority: 'high' });
      } else if (stage === 7) {
        showHint({ id: 'stage-7', text: 'Space technology is ready. Build launch infrastructure, a Space Port, and a Rocket Program.', priority: 'high' });
      } else if (stage === 8) {
        showHint({ id: 'stage-8', text: 'Space exploration is open. The orbital map and energy-harvesting research are now available.', priority: 'high' });
      }
    }
    prevStageRef.current = stage;

    // First visit hint
    if (surfaceItems.length === 0 && stage === 0) {
      showHint({ id: 'first-visit', text: 'Welcome! Start by building Crust Stabilisation or Volcanic Activity to prepare your planet.', priority: 'high' });
    }

    // Low credits hint
    if (credits < 20 && surfaceItems.length > 0) {
      showHint({ id: 'low-credits', text: 'Low on credits! Complete assessment tasks to earn more.', priority: 'low' });
    }

    // No land hint
    if (usableLand <= 0 && stage >= 1 && surfaceItems.length > 0) {
      showHint({ id: 'no-land', text: 'No land left! You can build Coastal Land or Continent to create more.', priority: 'medium' });
    }

    // Evolution event trigger — only once per planet, when stage first reaches 5
    const evolutionShownKey = `evolution_modal_shown_${planet?.planet_id}`;
    if (prevStageRef.current !== null && prevStageRef.current < 5 && stage >= 5 && !localStorage.getItem(evolutionShownKey)) {
      setEvolutionOpen(true);
      localStorage.setItem(evolutionShownKey, 'true');
    }
  }, [stage, credits, surfaceItems.length, usableLand, showHint, evolutionKey, planet]);

  // Debug logging
  if (import.meta.env.DEV) {
    console.log('[PlanetView] query state:', { isPending, isLoading, isError, hasData: !!data, errorCode: (error as any)?.response?.status });
  }

  // Auth error — clear and go back to landing page
  const isAuthError = (error as any)?.response?.status === 401;
  if (isAuthError) {
    clearGameSession();
    window.location.href = '/';
    return null;
  }

  // Still loading or no data yet
  if (!data && !isPending) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-950">
        <div className="text-red-400 text-sm">Failed to load planet. Please refresh.</div>
      </div>
    );
  }

  if (isPending) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-cyan-400 animate-pulse">Loading your planet...</div>
      </div>
    );
  }

  if (!planet) {
    clearGameSession();
    window.location.href = '/';
    return null;
  }

  if (spaceMapQuery.data?.spaceUnlocked && !showPlanetOverride) {
    return <SpaceGameView onOpenPlanet={() => setShowPlanetOverride(true)} />;
  }

  return (
    <div className="flex flex-col h-[100dvh] overflow-hidden">
      {/* Top bar */}
      <header className="flex items-center justify-between gap-2 bg-slate-900 px-3 pb-2 pt-[calc(env(safe-area-inset-top)+0.5rem)] md:px-6 md:py-3 border-b border-slate-800 shrink-0">
        <div className="flex items-center gap-2 md:gap-4 min-w-0">
          <h1 className="text-lg md:text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent whitespace-nowrap">
            Planet Builder
          </h1>
          <span className="hidden sm:inline text-sm text-slate-400">{stageNames[stage]}</span>
          <span className="hidden md:flex items-center gap-1 text-xs text-slate-500 bg-slate-800/60 px-2 py-0.5 rounded-full">
            <Calendar className="w-3 h-3" />
            Day {planet.current_day || 1}
          </span>
        </div>
        <div className="flex items-center gap-2 md:gap-4">
          <div className="flex items-center gap-1.5 px-2.5 md:px-3 py-1.5 bg-slate-800 rounded-full">
            <Coins className="w-4 h-4 text-yellow-400" />
            <span className="font-mono font-semibold">{credits}</span>
            {isDev && data?.data?.planet?.owner_user_id && (
              <button
                onClick={async () => {
                  try {
                    await devApi.addCredits(data.data!.planet.owner_user_id, 100);
                    queryClient.invalidateQueries({ queryKey: ['planet', 'me'] });
                  } catch (e) {
                    console.error('Failed to add credits', e);
                  }
                }}
                className="ml-1 p-0.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-300"
                title="Dev: +100 credits"
              >
                <Plus className="w-3 h-3" />
              </button>
            )}
          </div>
          <button
            onClick={() => {
              if (developmentOpen) {
                closeDevelopment();
              } else {
                openDevelopment();
              }
            }}
            className={`hidden sm:flex items-center gap-1.5 p-1.5 md:px-3 md:py-2 rounded-lg transition-colors ${
              developmentOpen ? 'bg-cyan-600 text-white' : 'bg-slate-800 hover:bg-slate-700'
            }`}
            title="Development"
          >
            <Wrench className="w-4 h-4 md:w-5 md:h-5" />
            <span className="hidden lg:inline text-sm font-semibold">Build</span>
          </button>
          <button
            onClick={() => {
              if (creaturePanelOpen) {
                closeCreaturePanel();
              } else {
                openCreaturePanel();
              }
            }}
            className={`hidden sm:flex p-1.5 md:p-2 rounded-lg transition-colors ${
              creaturePanelOpen ? 'bg-green-600 text-white' : 'bg-slate-800 hover:bg-slate-700'
            }`}
            title="Creatures"
          >
            <Bug className="w-4 h-4 md:w-5 md:h-5" />
          </button>
          {civilisationUnlocked && (
            <button
              onClick={() => {
                if (civilisationOpen) {
                  closeCivilisation();
                } else {
                  openCivilisation();
                }
              }}
              className={`hidden sm:flex p-1.5 md:p-2 rounded-lg transition-colors ${
                civilisationOpen ? 'bg-amber-600 text-white' : 'bg-slate-800 hover:bg-slate-700'
              }`}
              title="Civilisation"
            >
              <Landmark className="w-4 h-4 md:w-5 md:h-5" />
            </button>
          )}
          {stage >= 6 && (
            <button
              onClick={() => {
                if (techTreeOpen) {
                  closeTechTree();
                } else {
                  openTechTree();
                }
              }}
              className={`hidden sm:flex items-center gap-1.5 p-1.5 md:px-3 md:py-2 rounded-lg transition-colors ${
                techTreeOpen ? 'bg-cyan-600 text-white' : 'bg-slate-800 hover:bg-slate-700'
              }`}
              title="Tech Tree"
            >
              <Network className="w-4 h-4 md:w-5 md:h-5" />
              <span className="hidden lg:inline text-sm font-semibold">Tech</span>
            </button>
          )}
          <button
            onClick={() => {
              const canvas = document.querySelector('canvas');
              if (canvas) {
                const link = document.createElement('a');
                link.download = `planet-${planet.name.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}.png`;
                link.href = canvas.toDataURL('image/png');
                link.click();
              }
            }}
            className="hidden sm:flex p-1.5 md:p-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors"
            title="Snapshot"
          >
            <Camera className="w-4 h-4 md:w-5 md:h-5" />
          </button>
          <button
            onClick={() => openLeaderboard()}
            className="hidden sm:flex p-1.5 md:p-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors"
            title="Class Leaderboard"
          >
            <Trophy className="w-4 h-4 md:w-5 md:h-5" />
          </button>
          <button
            onClick={() => setEmergencyOpen(true)}
            className="hidden sm:flex p-1.5 md:p-2 rounded-lg bg-slate-800 hover:bg-red-900/40 transition-colors border border-red-800/30"
            title="Emergency Supplies"
          >
            <HeartPulse className="w-4 h-4 md:w-5 md:h-5 text-red-400" />
          </button>
          <button
            onClick={() => openStarMap()}
            className="hidden sm:flex relative p-1.5 md:p-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors"
            title="Star Map"
          >
            <Telescope className="w-4 h-4 md:w-5 md:h-5 text-indigo-400" />
            {(inboxQuery.data?.unreadCount ?? 0) > 0 && (
              <span className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 bg-red-500 rounded-full text-[9px] text-white font-bold animate-pulse">
                {inboxQuery.data?.unreadCount}
              </span>
            )}
          </button>
          <button
            onClick={() => openActivity()}
            className="hidden sm:flex p-1.5 md:p-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors"
            title="Recent Activity"
          >
            <History className="w-4 h-4 md:w-5 md:h-5" />
          </button>
          <button
            onClick={() => openEventLog()}
            className="hidden sm:flex p-1.5 md:p-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors relative"
            title="Planet History"
          >
            <ScrollText className="w-4 h-4 md:w-5 md:h-5 text-orange-400" />
            {eventHistory.length > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-600 text-white text-[9px] rounded-full flex items-center justify-center">
                {eventHistory.length > 99 ? '99+' : eventHistory.length}
              </span>
            )}
          </button>
          <button
            onClick={toggleMute}
            className="hidden sm:flex p-1.5 md:p-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors"
            title={muted ? 'Unmute sound' : 'Mute sound'}
          >
            {muted ? <VolumeX className="w-4 h-4 md:w-5 md:h-5 text-slate-500" /> : <Volume2 className="w-4 h-4 md:w-5 md:h-5 text-cyan-400" />}
          </button>
          {isDev && (
            <div className="hidden sm:block">
              <DevTimeControls />
            </div>
          )}
          {spaceMapQuery.data?.spaceUnlocked && showPlanetOverride && (
            <button
              onClick={() => {
                closePanels();
                setShowPlanetOverride(false);
              }}
              className="flex p-1.5 md:p-2 rounded-lg bg-cyan-900/60 hover:bg-cyan-800 transition-colors"
              title="Space Command"
            >
              <Rocket className="w-4 h-4 md:w-5 md:h-5 text-cyan-200" />
            </button>
          )}
          <button
            onClick={() => {
              clearGameSession();
              window.location.reload();
            }}
            className="p-1.5 md:p-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors"
            title="Logout"
          >
            <LogOut className="w-4 h-4 md:w-5 md:h-5" />
          </button>
        </div>
      </header>

      <nav className="sm:hidden grid grid-cols-8 gap-1 border-b border-slate-800 bg-slate-900/98 px-2 py-2 shrink-0">
        <button
          onClick={closePanels}
          aria-label="Show planet"
          className={`flex min-h-11 flex-col items-center justify-center gap-0.5 rounded-lg px-1 py-1.5 text-[10px] ${!developmentOpen && !creaturePanelOpen && !civilisationOpen && !techTreeOpen ? 'bg-cyan-900/50 text-cyan-200' : 'text-slate-400'}`}
        >
          <Home className="h-4 w-4" />
          Planet
        </button>
        <button
          onClick={() => {
            closeCreaturePanel();
            closeCivilisation();
            closeTechTree();
            developmentOpen ? closeDevelopment() : openDevelopment();
          }}
          aria-label="Open development"
          className={`flex min-h-11 flex-col items-center justify-center gap-0.5 rounded-lg px-1 py-1.5 text-[10px] ${developmentOpen ? 'bg-cyan-900/50 text-cyan-200' : 'text-slate-400'}`}
        >
          <Wrench className="h-4 w-4" />
          Build
        </button>
        <button
          onClick={() => {
            closeDevelopment();
            closeCivilisation();
            closeTechTree();
            creaturePanelOpen ? closeCreaturePanel() : openCreaturePanel();
          }}
          aria-label="Open creatures"
          className={`flex min-h-11 flex-col items-center justify-center gap-0.5 rounded-lg px-1 py-1.5 text-[10px] ${creaturePanelOpen ? 'bg-green-900/50 text-green-200' : 'text-slate-400'}`}
        >
          <Bug className="h-4 w-4" />
          Life
        </button>
        <button
          onClick={() => {
            closeDevelopment();
            closeCreaturePanel();
            closeCivilisation();
            closeTechTree();
            openStarMap();
          }}
          aria-label="Open star map"
          className="flex min-h-11 flex-col items-center justify-center gap-0.5 rounded-lg px-1 py-1.5 text-[10px] text-indigo-400"
        >
          <Telescope className="h-4 w-4" />
          Stars
        </button>
        <button
          onClick={() => {
            if (!civilisationUnlocked) return;
            closeDevelopment();
            closeCreaturePanel();
            closeStarMap();
            closeTechTree();
            civilisationOpen ? closeCivilisation() : openCivilisation();
          }}
          aria-label="Open civilisation"
          disabled={!civilisationUnlocked}
          className={`flex min-h-11 flex-col items-center justify-center gap-0.5 rounded-lg px-1 py-1.5 text-[10px] disabled:opacity-40 ${civilisationOpen ? 'bg-amber-900/50 text-amber-200' : 'text-slate-400'}`}
        >
          <Landmark className="h-4 w-4" />
          Civ
        </button>
        <button
          onClick={() => {
            if (stage < 6) return;
            closeDevelopment();
            closeCreaturePanel();
            closeCivilisation();
            closeStarMap();
            techTreeOpen ? closeTechTree() : openTechTree();
          }}
          aria-label="Open tech tree"
          disabled={stage < 6}
          className={`flex min-h-11 flex-col items-center justify-center gap-0.5 rounded-lg px-1 py-1.5 text-[10px] disabled:opacity-40 ${techTreeOpen ? 'bg-cyan-900/50 text-cyan-200' : 'text-slate-400'}`}
        >
          <Network className="h-4 w-4" />
          Tech
        </button>
        <button
          onClick={() => setEmergencyOpen(true)}
          aria-label="Emergency supplies"
          className="flex min-h-11 flex-col items-center justify-center gap-0.5 rounded-lg px-1 py-1.5 text-[10px] text-red-400 border border-red-800/30"
        >
          <HeartPulse className="h-4 w-4" />
          Emergency
        </button>
        <button
          onClick={openEventLog}
          aria-label="Open history"
          className="relative flex min-h-11 flex-col items-center justify-center gap-0.5 rounded-lg px-1 py-1.5 text-[10px] text-slate-400"
        >
          <ScrollText className="h-4 w-4" />
          History
          {eventHistory.length > 0 && (
            <span className="absolute right-1 top-1.5 h-1.5 w-1.5 rounded-full bg-orange-500" />
          )}
        </button>
      </nav>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Planet canvas area */}
        <div className="flex-1 relative min-w-0 overflow-hidden">
          <PlanetCanvas
            stage={stage}
            visualState={visualState}
            surfaceItems={visibleSurfaceItems}
            eventHistory={eventHistory}
            planetConfig={planetConfig}
            planetId={planet.planet_id}
            orbitEpochMs={planet.created_at ? Date.parse(planet.created_at) : undefined}
            dayDurationMs={planet.day_duration_ms}
            currentDay={currentDay}
          />
          <HintPanel />
          {latestEventText && (
            <div className="absolute left-3 top-3 z-20 max-w-sm rounded-lg border border-orange-500/40 bg-slate-900/80 px-3 py-2 text-xs text-orange-100 shadow-lg">
              <div className="flex items-start justify-between gap-2">
                <div className="font-semibold text-orange-300">Active Planet Event</div>
                <button
                  onClick={() => setDismissedEventId(latestEventId)}
                  className="p-0.5 rounded hover:bg-orange-900/30 text-orange-300/70 hover:text-orange-100 transition-colors"
                  title="Dismiss"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="mt-1">{latestEventText}</div>
              {latestEventEffects && (
                <div className="mt-1 font-mono text-[11px] text-orange-200">{latestEventEffects}</div>
              )}
              <div className="mt-1 text-[11px] text-slate-300">Watch stats, life, and population panels for impact changes.</div>
            </div>
          )}
          <StatsPanel stats={stats} geography={geography} lifeChance={lifeChance} statTrends={statTrends} />
        </div>

        {/* Side panels — overlay only; never resize the planet canvas */}
        {creaturePanelOpen && (
          <aside className="fixed inset-0 z-30 bg-slate-900/95 overflow-y-auto pb-[env(safe-area-inset-bottom)] shadow-2xl md:absolute md:inset-y-0 md:right-0 md:left-auto md:w-80 md:border-l md:border-slate-800 md:bg-slate-900/92 md:backdrop-blur-sm">
            <div className="sticky top-0 z-10 flex items-center justify-between p-3 border-b border-slate-800 bg-slate-900/95 backdrop-blur">
              <span className="text-sm font-semibold">Creatures</span>
              <button onClick={closeCreaturePanel} className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700" title="Close creatures panel">
                <ChevronDown className="w-5 h-5" />
              </button>
            </div>
            <CreaturePanel surfaceItems={visibleSurfaceItems} planetState={{ stats }} stage={stage} intelligentSpecies={intelligentSpecies} />
          </aside>
        )}
        {developmentOpen && (
          <div
            className="fixed inset-0 z-30 bg-slate-950/75 shadow-2xl backdrop-blur-sm md:flex md:items-center md:justify-center md:p-6"
            onPointerDown={closeDevelopment}
          >
            <aside
              className="flex h-full w-full flex-col overflow-hidden bg-slate-900/98 pb-[env(safe-area-inset-bottom)] md:h-[min(82vh,760px)] md:w-[min(96vw,1120px)] md:rounded-xl md:border md:border-slate-700 md:pb-0 md:shadow-2xl"
              onPointerDown={(event) => event.stopPropagation()}
            >
              <div className="flex shrink-0 items-center justify-between p-3 border-b border-slate-800 bg-slate-900/95 backdrop-blur">
                <span className="text-sm font-semibold">Development</span>
                <button onClick={closeDevelopment} className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700" title="Close development panel">
                  <ChevronDown className="w-5 h-5 md:hidden" />
                  <span className="hidden md:inline text-sm px-1">Close</span>
                </button>
              </div>
              <div className="min-h-0 flex-1">
                <DevelopmentPanel currentBalance={credits} stats={stats} stage={stage} lifeChance={lifeChance || 0} />
              </div>
            </aside>
          </div>
        )}
        {civilisationOpen && (
          <aside className="fixed inset-0 z-30 bg-slate-900/95 overflow-y-auto pb-[env(safe-area-inset-bottom)] shadow-2xl md:absolute md:inset-y-0 md:right-0 md:left-auto md:w-96 md:border-l md:border-slate-800 md:bg-slate-900/92 md:backdrop-blur-sm">
            <CivilisationPanel onClose={closeCivilisation} spaceAge={Boolean(spaceMapQuery.data?.spaceUnlocked)} />
          </aside>
        )}
        {techTreeOpen && (
          <div
            className="fixed inset-0 z-30 bg-slate-950/75 shadow-2xl backdrop-blur-sm md:flex md:items-center md:justify-center md:p-6"
            onPointerDown={closeTechTree}
          >
            <aside
              className="flex h-full w-full flex-col overflow-hidden bg-slate-900/98 pb-[env(safe-area-inset-bottom)] md:h-[min(82vh,760px)] md:w-[min(96vw,1120px)] md:rounded-xl md:border md:border-slate-700 md:pb-0 md:shadow-2xl"
              onPointerDown={(event) => event.stopPropagation()}
            >
              <div className="flex shrink-0 items-center justify-between p-3 border-b border-slate-800 bg-slate-900/95 backdrop-blur">
                <span className="text-sm font-semibold">Tech Tree</span>
                <button onClick={closeTechTree} className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700" title="Close tech tree panel">
                  <ChevronDown className="w-5 h-5 md:hidden" />
                  <span className="hidden md:inline text-sm px-1">Close</span>
                </button>
              </div>
              <div className="min-h-0 flex-1">
                <TechTreePanel currentBalance={credits} stats={stats} />
              </div>
            </aside>
          </div>
        )}
      </div>

      {/* Leaderboard modal */}
      {leaderboardOpen && <LeaderboardPanel onClose={closeLeaderboard} />}

      {/* Activity modal */}
      {activityOpen && <ActivityPanel onClose={closeActivity} />}

      {/* Event Log modal */}
      {eventLogOpen && (
        <EventLogPanel
          events={eventHistory}
          currentDay={currentDay}
          onClose={closeEventLog}
        />
      )}

      {/* Star Map modal */}
      {starMapOpen && <StarMapModal stage={stage} onClose={closeStarMap} />}

      {/* Evolution event modal */}
      {evolutionOpen && <EvolutionModal dominantCreature={dominantCreature} onClose={() => setEvolutionOpen(false)} />}

      {/* Emergency supplies modal */}
      {emergencyOpen && (
        <EmergencyModal
          planetId={planet.planet_id}
          stats={stats}
          geography={geography}
          credits={credits}
          stage={stage}
          onClose={() => setEmergencyOpen(false)}
        />
      )}

      {/* Toast notifications */}
      <ToastContainer />
    </div>
  );
}
